<template>
    <b-card
        no-body
    >
        <div class="card-header">
            <div
                class="card-group-title"
            >
                <h4>{{ $t('v4.API Key') }}</h4>
            </div>
            <b-badge variant="primary" style="cursor:pointer" class="mr-2">
                <router-link :to="`user-guide?path=docs%2F${namespace}%2F${locale}%2Fopen-api%2FuserGuide.json`" class="text-light" target="_blank">{{ $t('v4.User Guide') }}</router-link>
            </b-badge>
        </div>
        <slot name="alert"></slot>
        <b-card-body
            v-if="hasApiKey"
        >
                <b-input-group
                    class="mb-1"
                >
                <b-form-input
                    :value="key"
                    ref="REF_INPUT_API_KEY"
                />
                <b-input-group-append>
                    <b-button
                        v-if="showKey"
                        variant="outline-primary"
                        @click="hideInputKey"
                    >
                        <feather-icon
                            icon="EyeOffIcon"
                        />
                    </b-button>
                    <b-button
                        v-else
                        variant="outline-primary"
                        @click="showInputKey"
                    >
                        <feather-icon
                            icon="EyeIcon"
                        />
                    </b-button>
                    <b-button
                        v-if="showKey"
                        variant="outline-primary"
                        @click="copyToClipboard(key)"
                    >
                        <feather-icon
                            icon="CopyIcon"
                        />
                    </b-button>
                </b-input-group-append>
            </b-input-group>
            <b-card-text
                class="d-flex justify-content-end"
            >
                        <feather-icon
                            v-if="extendable"
                            class="text-warning my-25 mx-25"
                            icon="AlertCircleIcon"
                        />
                        {{ $t('v4.Expiration date') }}:
                        {{ endDate }}
            </b-card-text>
        </b-card-body>
        <b-card-body v-else>
            <div>
                <p>
                    {{ $t('v4["Set up keys for programmatic access to your billing data."]') }}
                </p>
            </div>
        </b-card-body>
        <b-card-footer>
            <div v-if="!hasApiKey">
                <b-button color="dark" active v-if="!hasApiKey" @click="generateNewApiKey">
                    {{ $t('v4.Generate keys') }}
                </b-button>
            </div>
            <div v-else class="d-flex justify-content-between">
                <b-button
                    variant="danger"
                    style="margin-left:3px;"
                    @click="removeApiKey"
                >
                    {{ $t('v4.Delete') }}
                </b-button>
                <b-button
                    variant="primary"
                    style="margin-left:3px;"
                    @click="syncAccount"
                >
                    <feather-icon
                        icon="RefreshCcwIcon"
                        class="mr-25"
                    />
                    {{ $t('v4.Sync account') }}
                </b-button>
            </div>
        </b-card-footer>
    </b-card>
</template>

<script>
import bus from '@/libs/bus'
import useAppConfig from '@core/app-config/useAppConfig'
import { ref, computed, getCurrentInstance, toRefs } from '@vue/composition-api'
export default {
    model: {
        prop: 'showKey',
        event: 'sync:showKey'
    },
    props: {
        showKey: {
            type: Boolean
        },
        extendablePeriod: {
            type: Number
        },
        startDate: {
            type: String
        },
        endDate: {
            type: String
        },
        availablePeriod: {
            type: Number
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const namespace = process.env.VUE_APP_NAMESPACE
        const locale = computed(() => vm.$t('org.locale'))
        const REF_INPUT_API_KEY = ref(null)
        const hasApiKey = computed(() => store.getters['$_open_api/hasApiKey'])
        const apiKey = computed(() => store.getters['$_open_api/apiKey'])

        const { availablePeriod, extendablePeriod } = toRefs(props)

        const { skin } = useAppConfig()

        const key = computed(() => {
            if (props.showKey) {
                return apiKey.value
            } else {
                return vm.$t('v4.Hidden for security reasons')
            }
        })

        const extendable = computed(() => availablePeriod.value <= extendablePeriod)
        const getOpenApiKeyStatus = () => {
            store.dispatch('$_open_api/getOpenApiKeyStatus').then(() => {
                context.emit('api:fetchUsages')
            })
        }
        const createOpenApiKey = () => store.dispatch('$_open_api/createOpenApiKey')
        const deleteOpenApiKey = () => store.dispatch('$_open_api/deleteOpenApiKey')
        const updateSyncAccount = () => store.dispatch('$_open_api/updateSyncAccount')

        const copyToClipboard = (value) => {
            if (REF_INPUT_API_KEY.value) {
                const t = document.createElement('textarea')
                document.body.appendChild(t)
                t.value = value
                t.select()
                document.execCommand('copy')
                document.body.removeChild(t)
                REF_INPUT_API_KEY.value.$el.focus()
            }
        }
        const generateNewApiKey = () => {
            createOpenApiKey().then(() => {
                const message = vm.$t('org.message.API key has been created')
                bus.$emit('request:success', { title: 'SUCCESS', content: message })
                vm.$emit('alert:message', { status: 'success', message })
                getOpenApiKeyStatus()
            })
        }
        const showInputKey = () => {
            context.emit('sync:showKey', true)
        }
        const hideInputKey = () => {
            context.emit('sync:showKey', false)
        }
        const syncAccount = () => {
            updateSyncAccount().then(() => {
                const message = vm.$t('org.message.Account synchronization was successful')
                bus.$emit('request:success', { title: 'SUCCESS', content: message })
                vm.$emit('alert:message', { status: 'success', message })
            }).catch(error => {
                if (error?.response?.data?.errorMessage) {
                    vm.$emit('alert:message', { status: 'danger', message: error.response.data.errorMessage })
                }
            })
        }
        const removeApiKey = () => {
            deleteOpenApiKey().then(() => {
                const message = vm.$t('org.message.API key has been deleted')
                bus.$emit('request:success', { title: 'SUCCESS', content: message })
                vm.$emit('alert:message', { status: 'success', message })
                hideInputKey()
                getOpenApiKeyStatus()
            })
        }
        const goToUserGuide = () => {
            vm.$router.push({ name: 'open-api/user-guide' })
        }
        return {
            REF_INPUT_API_KEY,
            namespace,
            locale,
            key,
            hasApiKey,
            extendable,
            syncAccount,
            copyToClipboard,
            generateNewApiKey,
            showInputKey,
            hideInputKey,
            removeApiKey,
            goToUserGuide,
            skin
        }
    }
}
</script>

<style>

</style>
